/**
 * Writing a builder's open options back when nobody pressed Save.
 *
 * The builder stores an option's food rows when that option's own Save is
 * pressed — everything typed before it is the browser's alone. That is fine
 * while the screen is up and lost the moment it is not: a click on the sidebar
 * takes the page away, and with it whatever was written into the card that was
 * still open. Every option saved before it survives, because each of those was
 * a call; only the last one goes, which is the one that was being worked on.
 *
 * So the builder flushes on its way out — see `PlanDetailPage`. What is here is
 * the writing itself, kept out of the page because it has to outlive it: the
 * page is unmounting while these calls are in flight, and nothing below touches
 * React.
 *
 * `createMissingOptions` is shared with the page's own Save rather than written
 * twice: an option the backend has never heard of has to be created before
 * anything can be hung off it, whichever of the two is doing the saving.
 */

import { EMPTY_MACROS, isOptionEmpty } from "../planMapping";
import { addMealOption, saveMealOptionItems } from "./mealPlansService";
import type { GetToken } from "../../auth/types/auth.types";
import type { MealOption, MealSlot, PlanRecord } from "../types/plans.types";

/**
 * An option's rows as they are compared — the fields that are stored, in the
 * order the table holds them — with the instructions written under them on
 * one last line, since those are stored by the same call.
 *
 * A row written out by hand carries its own four figures, so a macro typed
 * into one is an edit like any other. A catalog row's are left out on purpose:
 * they are the backend's own arithmetic, arriving a beat after the row was
 * filled in, and counting them would read that as something to save.
 *
 * The instructions are compared trimmed, which is how they are sent: a space
 * typed after the last word is not an edit the backend would keep.
 */
export const rowsOf = (option: MealOption): string =>
  [
    ...option.dishes.map((dish) => {
      const row = `${dish.dish}|${dish.unit}|${dish.quantity}`;
      if (!dish.custom) return row;

      const { calories, carbs, protein, fat } = dish.macros ?? EMPTY_MACROS;
      return `${row}|${calories}|${carbs}|${protein}|${fat}`;
    }),
    `#${option.instructions?.trim() ?? ""}`,
  ].join("\n");

/**
 * What the backend holds for each of a plan's options, by option id.
 *
 * The baseline every unsaved-rows question is asked against. Taken off a plan
 * as it was read back, and moved on by hand as each option is saved — not off
 * the copy in `plansService`, which is the browser's own and carries rows the
 * backend has never been told about: adding a meal writes the whole plan there,
 * half-typed cards included, so measuring against it would call those rows
 * saved when the only thing that has them is this tab.
 *
 * Options the browser invented are left out: there is nothing stored for them.
 */
export const serverRows = (
  record: PlanRecord | undefined
): Map<string, string> =>
  new Map(
    (record?.meals ?? []).flatMap((meal) =>
      meal.options
        .filter((option) => !option.isLocal)
        .map((option) => [option.id, rowsOf(option)] as const)
    )
  );

/**
 * Whether an option holds rows, or instructions, the backend does not.
 *
 * Only a card open as a form can: a row is written into the copy on screen as
 * it is typed and goes no further until something saves that option. An option
 * the backend has never heard of has nothing to compare against, so anything
 * written into it is unsaved by definition — and the blank one every meal opens
 * with holds nothing to lose. Instructions typed under an empty table count
 * too: they are as much the dietitian's work as a row, and the save keeps them
 * whether or not a row went up beside them.
 */
export const hasUnsavedRows = (
  option: MealOption,
  baseline: ReadonlyMap<string, string>,
  openIds: ReadonlySet<string>
): boolean => {
  if (!openIds.has(option.id)) return false;

  const stored = baseline.get(option.id);
  if (stored !== undefined) return stored !== rowsOf(option);
  return !isOptionEmpty(option) || !!option.instructions?.trim();
};

/** Whether anything on the plan is worth a flush — asked before one is started,
 *  so leaving a plan nobody wrote in costs no calls. */
export const hasUnsavedWork = (
  record: PlanRecord,
  baseline: ReadonlyMap<string, string>,
  openIds: ReadonlySet<string>
): boolean =>
  record.meals.some((meal) =>
    meal.options.some((option) => hasUnsavedRows(option, baseline, openIds))
  );

export interface CreatedOptions {
  /** The meal's options, the created ones under the ids the backend gave them.
   *  The meal's own list where nothing was created, so a caller comparing by
   *  reference can tell a call that changed nothing. */
  options: MealOption[];
  /** Browser id → backend id, for whatever was created. */
  createdIds: Map<string, string>;
  /** Whatever refused, if the run stopped short. */
  error?: unknown;
}

/**
 * Creates on the backend whichever of a meal's options it does not have.
 *
 * Only ever the blank one a meal opens with, in practice: the builder shows one
 * so there is something to write in, and the backend is not told about it until
 * something needs it to be real — a dish saved into it, or a second option
 * added beside it. Creating it first is what makes that second one the
 * backend's second option rather than its first.
 *
 * It answers with what the meal's options are now and with whatever refused it,
 * rather than throwing: an option created before something after it fell over
 * is one the backend already has, and losing track of it here would make it
 * twice on the next press.
 */
export async function createMissingOptions(
  getToken: GetToken,
  planId: string,
  meal: MealSlot
): Promise<CreatedOptions> {
  const options: MealOption[] = [];
  const createdIds = new Map<string, string>();
  const settled = () => (createdIds.size > 0 ? options : meal.options);

  for (let index = 0; index < meal.options.length; index += 1) {
    const option = meal.options[index];
    if (!option.isLocal) {
      options.push(option);
      continue;
    }

    try {
      const created = await addMealOption(getToken, planId, meal.id, {
        // Where the option sits, which is all this call is about — the heading
        // the page gives it, and its place in the meal.
        name: `Option ${index + 1}`,
        sort_order: index + 1,
      });
      // The rows already typed into it stay, and the instructions with them:
      // the option is being created to hold them, and the backend answers
      // with none on it.
      options.push({
        ...created,
        dishes: option.dishes,
        instructions: option.instructions,
      });
      createdIds.set(option.id, created.id);
    } catch (error) {
      // The rest are left as they were — still the browser's, and still there
      // to be created by whatever is tried next.
      options.push(...meal.options.slice(index));
      return { options: settled(), createdIds, error };
    }
  }

  return { options: settled(), createdIds };
}

export interface PlanFlush {
  /** The plan as it now stands, the saved rows carrying the backend's ids.
   *  Worth storing whether or not everything went up: what failed is still on
   *  it, so a later save has it to send. */
  record: PlanRecord;
  /** Whether anything reached the backend, which is what makes the plan's
   *  stored totals worth asking for again. False for a plan whose meals are
   *  all the browser's own, and for one whose every call was refused. */
  written: boolean;
  /** The first thing that refused, if any did. */
  error?: unknown;
}

/** A meal's options written back into the plan they belong to. */
const withOptions = (
  record: PlanRecord,
  mealId: string,
  options: MealOption[]
): PlanRecord => ({
  ...record,
  meals: record.meals.map((meal) =>
    meal.id === mealId ? { ...meal, options } : meal
  ),
});

/**
 * Saves every open option holding rows the backend has not got.
 *
 * The same two calls the page's own Save makes, in the same order: the option
 * itself where the backend has not got it, then the table and instructions
 * written into it. Each meal is taken on its own so one that refuses does not
 * carry off the rest — the plan comes back with as much of it stored as the
 * backend would take, and with the first refusal to report.
 *
 * A meal the browser invented is passed over: there is nowhere on the backend
 * to hang an option off one, so its rows stay where they always were, in the
 * copy the caller stores.
 */
export async function flushOpenOptions(
  getToken: GetToken,
  record: PlanRecord,
  baseline: ReadonlyMap<string, string>,
  openIds: ReadonlySet<string>
): Promise<PlanFlush> {
  let next = record;
  let written = false;
  let error: unknown;

  for (const meal of record.meals) {
    const dirty = meal.options.some((option) =>
      hasUnsavedRows(option, baseline, openIds)
    );
    // A meal the browser invented is passed over: there is nowhere on the
    // backend to hang an option off one, so its rows stay where they always
    // were — in the copy the caller stores, which is the whole of what saving
    // one of these has ever been.
    if (!dirty || meal.isLocal) continue;

    const created = await createMissingOptions(getToken, record.id, meal);
    let options = created.options;
    written = written || created.createdIds.size > 0;
    error = error ?? created.error;

    // Position for position with the meal's own list, whether or not anything
    // was created: an option keeps its place in the meal, and only its id
    // changes. That is what lets a row typed into a card the backend had never
    // heard of be recognised by where it sits rather than by an id that has
    // since moved on.
    for (let index = 0; index < options.length; index += 1) {
      const before = meal.options[index];
      const option = options[index];

      // Still the browser's — the create above was refused — so there is
      // nowhere to send its rows yet.
      if (!before || !option || option.isLocal) continue;
      if (!hasUnsavedRows(before, baseline, openIds)) continue;

      try {
        const saved = await saveMealOptionItems(
          getToken,
          record.id,
          meal.id,
          option.id,
          option
        );
        options = options.map((current) =>
          current.id === option.id ? { ...current, ...saved } : current
        );
        written = true;
      } catch (reason) {
        // Left as it was typed, and kept on the record: the rows are still the
        // browser's, which is exactly what they were a moment ago.
        error = error ?? reason;
      }
    }

    next = withOptions(next, meal.id, options);
  }

  return { record: next, written, error };
}
